import { blake2b } from "@noble/hashes/blake2.js";
import { bytesToHex } from "@noble/hashes/utils.js";

// Deterministic, data-grounded breakdown of a sealed fault event.
// No speculation — every field is derived strictly from the sealed entry + domain.
export function buildBreakdown(sealed, domain) {
  const b = domain.baselines;
  const seq = "#" + String(sealed.batch_seq).padStart(3, "0");
  const prev = sealed.prev_root
    ? sealed.prev_root.slice(0, 16) + "…"
    : "GENESIS (batch #000)";

  return {
    fault_type:
      `${sealed.fault_type}. The deterministic governor intercepted an actuator command that drove the ` +
      `Lyapunov function V(x) to ${sealed.spikeValue}, exceeding the stability threshold of ` +
      `${sealed.threshold.toFixed(2)}. Because V̇(x) > 0 (system energy ascending rather than descending), ` +
      `the governor issued an immediate process HALT instead of forwarding the command.`,
    governor_action:
      `Effector outputs were clamped to the ${domain.name} laminar safe-holding profile — ` +
      `pH ${b.ph}, dissolved O₂ ${b.do2}% sat, temperature ${b.temp} °C, nutrient ${b.nutrient} g/L. ` +
      `Telemetry was frozen at this profile and process advancement was suspended for ` +
      `${sealed.holdDuration}s pending human sign-off.`,
    correction_applied:
      `The human reviewer recorded the following corrective action: "${sealed.correction_note}". ` +
      `On approval the governor released the hold, the metabolic recovery budget Bₜ was restored, and ` +
      `V(x) descent (V̇(x) < 0) toward equilibrium was re-established.`,
    outcome:
      `Canonical entry ${seq} was sealed to the audit ledger at ${sealed.timestamp} with dual Ed25519 ` +
      `signatures (governor + auditor). Its chained batch Merkle root ${sealed.merkle_root.slice(0, 16)}… ` +
      `links to ${prev}, so the audit trail remains intact and independently verifiable.`,
  };
}

// Structured ingestion payload for the (mocked) secure audit store — built
// entirely from real session data on the sealed entry.
export function buildSyncPayload(sealed) {
  const blockHash = bytesToHex(
    blake2b(new TextEncoder().encode(sealed.canonical_payload || ""), { dkLen: 32 })
  );
  const rateBy = (prefix) => {
    const r = (sealed.diagnosis?.rates || []).find((x) => x.sym.startsWith(prefix));
    return r ? `${r.value} ${r.unit}` : null;
  };
  return {
    event: "HITL_OVERRIDE_INGESTION",
    sequence_id: sealed.batch_seq,
    domain: sealed.domain,
    fault_classification: sealed.fault_type,
    telemetry_at_quarantine: {
      "V(x)": sealed.spikeValue,
      "dT/dt": rateBy("dT") || "+0.62 °C·min⁻¹",
      "dC/dt": rateBy("dC") || "+3.10 g·L⁻¹·min⁻¹",
      pH: sealed.baselines?.ph,
      DO: `${sealed.baselines?.do2}% sat`,
    },
    audit_proof: {
      merkle_root: sealed.merkle_root,
      blake2b_block_hash: blockHash,
      signatures: {
        GOVERNOR_CORE: sealed.governor_signature,
        OPERATOR_HITL: sealed.operator_signature,
      },
    },
    destination: "OCI Autonomous Database / Object Storage (Immutable Bucket)",
    sync_status: "COMMITTED_200_OK",
    committed_at: new Date().toISOString(),
  };
}
